import React, { memo } from "react";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CustomIcon from "./CustomIcon";
import { NAVIGATION_THEME } from "../constants/navigation";

/**
 * Individual navigation item component
 * Memoized for performance optimization
 */
const NavigationItem = memo(
  ({
    item,
    isSubItem = false,
    isActive = false,
    isParentActive = false,
    shouldHaveWhiteIcons = false,
    isExpanded = false,
    onItemClick,
    onToggleExpanded,
    showTooltip = false,
  }) => {
    // Determine if this is an active sub-item
    const isActiveSubItem = isSubItem && isActive;

    // Handle click events
    const handleClick = (event) => {
      if (item.children) {
        onToggleExpanded(item);
      }
      onItemClick(item, event);
    };

    // Handle expand/collapse click
    const handleExpandClick = (event) => {
      event.stopPropagation();
      onToggleExpanded(item);
    };

    // Get icon component - don't show icons for children
    const renderIcon = () => {
      if (isSubItem) return null; // No icons for children
      if (!item.icon && !item.iconName) return null;

      // Use the shouldHaveWhiteIcons prop to determine icon color
      // This handles all cases: active items, parent items with active children, etc.
      const iconColor = shouldHaveWhiteIcons
        ? NAVIGATION_THEME.colors.textActive
        : NAVIGATION_THEME.colors.text;

      const iconProps = {
        fontSize: "small",
        sx: {
          color: iconColor,
          minWidth: NAVIGATION_THEME.spacing.iconWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      };

      if (item.icon) {
        return React.createElement(item.icon, iconProps);
      }

      if (item.iconName) {
        return (
          <CustomIcon
            name={item.iconName}
            fill={iconColor}
            fontSize="small"
            sx={{
              color: iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        );
      }

      return null;
    };

    // Get expand/collapse icon
    const renderExpandIcon = () => {
      if (!item.children) return null;

      const ExpandIcon = isExpanded ? ExpandLess : ExpandMore;
      // Expand icons should be white when the item should have white icons
      const expandIconColor = shouldHaveWhiteIcons
        ? NAVIGATION_THEME.colors.textActive
        : NAVIGATION_THEME.colors.text;

      return (
        <ExpandIcon
          onClick={handleExpandClick}
          sx={{
            color: expandIconColor,
            fontSize: "1.2rem",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        />
      );
    };

    // Main button styling
    const buttonSx = {
      // Full maroon background for active parent items
      backgroundColor: isParentActive
        ? NAVIGATION_THEME.colors.backgroundActive
        : NAVIGATION_THEME.colors.background,
      // No left border for children (sub-items)
      borderLeft: isSubItem
        ? "none"
        : isActiveSubItem
        ? `6px solid ${NAVIGATION_THEME.colors.border}`
        : "none",
      // Remove all other borders
      borderRight: "none !important",
      borderTop: "none !important",
      borderBottom: "none !important",
      // Indentation for sub-items - more indentation for better visual hierarchy
      pl: isSubItem
        ? NAVIGATION_THEME.spacing.subItemPadding + 1
        : NAVIGATION_THEME.spacing.itemPadding,
      // Text color
      color: isParentActive
        ? NAVIGATION_THEME.colors.textActive
        : NAVIGATION_THEME.colors.text,
      // Hover effects
      "&:hover": {
        backgroundColor: isParentActive
          ? NAVIGATION_THEME.colors.primaryHover
          : NAVIGATION_THEME.colors.backgroundHover,
      },
      // Remove default selected styling and borders
      "&.Mui-selected": {
        backgroundColor: isParentActive
          ? NAVIGATION_THEME.colors.backgroundActive
          : NAVIGATION_THEME.colors.background,
        borderLeft: isSubItem
          ? "none"
          : isActiveSubItem
          ? `6px solid ${NAVIGATION_THEME.colors.border}`
          : "none",
        borderRight: "none !important",
        borderTop: "none !important",
        borderBottom: "none !important",
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "10px",
          backgroundColor:
            isParentActive && !isSubItem
              ? "#732F2F" // Lighter maroon highlight
              : "transparent",
          zIndex: 1,
        },
        "&:after": {
          content: isSubItem ? '""' : "none",
          position: "absolute",
          // Center the active bar horizontally with the non-active bars
          left: isActive ? "18.5px" : "20px", // 20 - (4-1)/2 = 18.5px for active bar
          top: 0,
          bottom: 0,
          width: isActive ? "4px" : "1px",
          backgroundColor: isActive ? "#500000" : "#cccccc",
          zIndex: 1,
        },
        "&:hover": {
          backgroundColor: isParentActive
            ? NAVIGATION_THEME.colors.primaryHover
            : NAVIGATION_THEME.colors.backgroundHover,
        },
      },
      // Remove focus border
      "&:focus": {
        outline: "none",
      },
      // Add thin highlight strip for active non-child items
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "10px",
        backgroundColor:
          isParentActive && !isSubItem
            ? "#732F2F" // Lighter maroon highlight
            : "transparent",
        zIndex: 1,
      },
      // Add vertical bar for child items - center active bar with non-active bars
      "&::after": {
        content: isSubItem ? '""' : "none",
        position: "absolute",
        // Center the active bar horizontally with the non-active bars
        left: isActive ? "18.5px" : "20px", // 20 - (4-1)/2 = 18.5px for active bar
        top: 0,
        bottom: 0,
        width: isActive ? "4px" : "1px",
        backgroundColor: isActive ? "#500000" : "#cccccc",
        zIndex: 1,
      },
      // Ensure proper spacing and alignment
      minHeight: 48,
      display: "flex",
      alignItems: "center",
      // Ensure consistent padding
      pr: 2,
      // Position relative for absolute pseudo-elements
      position: "relative",
    };

    // Text styling
    const textSx = {
      "& .MuiListItemText-primary": {
        fontWeight: isActive
          ? NAVIGATION_THEME.typography.fontWeight.bold
          : NAVIGATION_THEME.typography.fontWeight.normal,
        fontSize: isSubItem
          ? "0.8rem" // Smaller font for children
          : NAVIGATION_THEME.typography.fontSize,
        color: isSubItem
          ? "#666666" // Grey color for children (same as inactive)
          : "inherit",
      },
    };

    // Create the main button content
    const buttonContent = (
      <ListItemButton
        onClick={handleClick}
        selected={isActive}
        sx={buttonSx}
        role="menuitem"
        aria-label={item.text}
        aria-expanded={item.children ? isExpanded : undefined}
        aria-current={isActive ? "page" : undefined}
        data-tour-id={`nav-${item.text}`}
      >
        {renderIcon() && (
          <ListItemIcon
            sx={{
              minWidth: NAVIGATION_THEME.spacing.iconWidth,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {renderIcon()}
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.text}
          sx={{
            ...textSx,
            "& .MuiListItemText-primary": {
              ...textSx["& .MuiListItemText-primary"],
              lineHeight: 1.2,
            },
          }}
        />
        {renderExpandIcon()}
      </ListItemButton>
    );

    // Wrap with tooltip if needed
    if (showTooltip && item.tooltip) {
      return (
        <Tooltip title={item.tooltip} placement="right">
          {buttonContent}
        </Tooltip>
      );
    }

    return buttonContent;
  }
);

NavigationItem.displayName = "NavigationItem";

export default NavigationItem;
